import React, { useState, useEffect } from 'react';
import {
	motion,
	useMotionValue,
	useSpring,
	useTransform,
} from 'framer-motion';

const impactStats = [
	{
		metric: '3M+',
		label: 'Users on platforms I architected',
	},
	{
		metric: '$20B+',
		label: 'Annual transactions through my systems',
	},
	{
		metric: '83%',
		label: 'Processing time cut via re-architecture',
	},
	{
		metric: '99.9%',
		label: 'Uptime through peak and failure paths',
	},
];

const Hero = () => {
	const mouseX = useMotionValue(0);
	const mouseY = useMotionValue(0);
	const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20 });
	const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20 });

	const glowX = useTransform(smoothX, (v) => `${v}px`);
	const glowY = useTransform(smoothY, (v) => `${v}px`);

	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);

	const handleMouseMove = (e) => {
		const rect = e.currentTarget.getBoundingClientRect();
		mouseX.set(e.clientX - rect.left);
		mouseY.set(e.clientY - rect.top);
	};

	return (
		<section
			className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
			role="banner"
			onMouseMove={handleMouseMove}>

			{mounted && (
				<motion.div
					className="pointer-events-none absolute w-[500px] h-[500px] rounded-full opacity-[0.04]"
					style={{
						left: glowX,
						top: glowY,
						x: '-50%',
						y: '-50%',
						background: 'radial-gradient(circle, #4A6741 0%, transparent 70%)',
					}}
				/>
			)}

			<div className="w-full px-6 sm:px-16 py-32 relative z-10">
				<div className="max-w-6xl mx-auto w-full">
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.3, delay: 0.2 }}
						className="text-accent text-[14px] sm:text-[15px] font-semibold uppercase tracking-[0.2em] mb-4">
						Software Architect &middot; System Builder
					</motion.p>

				<h1 className="font-extrabold text-primary lg:text-[52px] sm:text-[42px] xs:text-[34px] text-[28px] lg:leading-[1.12] tracking-tight">
					I design and build high-scale systems <br />
					<span className="text-accent">that operate under real-world constraints.</span>
				</h1>

					<motion.p
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 0.8 }}
						className="mt-8 text-muted text-[17px] sm:text-[19px] leading-[30px] w-full text-justify">
						I am Sachin Saurav, a Software Architect at Entrata. I own systems
						end-to-end, from architecture decisions and tradeoff analysis to
						production code and operational reliability. I specialize in
						event-driven platforms, async workflow design, and AI-assisted
						operational tooling for PropTech at scale.
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 1.0 }}
						className="mt-10 flex flex-wrap gap-4">
						<motion.a
							href="#projects"
							whileHover={{ scale: 1.03 }}
							whileTap={{ scale: 0.97 }}
							className="inline-flex items-center gap-2 bg-accent text-white px-6 py-3 rounded-lg font-medium text-[15px] hover:bg-accent-dark transition-colors duration-200 shadow-sm shadow-accent/20">
							View Systems I Built
							<motion.svg
								animate={{ y: [0, 3, 0] }}
								transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
								className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
							</motion.svg>
						</motion.a>
						<motion.a
							href="#contact"
							whileHover={{ scale: 1.03 }}
							whileTap={{ scale: 0.97 }}
							className="inline-flex items-center gap-2 bg-warm text-primary border border-border px-6 py-3 rounded-lg font-medium text-[15px] hover:bg-surface transition-colors duration-200">
							Get in Touch
							<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
							</svg>
						</motion.a>
					</motion.div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.6, delay: 1.3 }}
						className="mt-16">
						<p className="text-primary text-[12px] uppercase tracking-[0.2em] font-semibold mb-4">
							Impact at Scale
						</p>
						<div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
							{impactStats.map((stat, idx) => (
								<motion.div
									key={stat.label}
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.3, delay: 1.35 + idx * 0.08 }}
									className="bg-surface border border-border rounded-xl px-4 py-4">
									<p className="text-accent text-[30px] leading-none font-extrabold">{stat.metric}</p>
									<p className="text-primary text-[13px] mt-2 font-normal">{stat.label}</p>
								</motion.div>
							))}
						</div>
					</motion.div>
				</div>
			</div>

			<div className="absolute bottom-8 left-1/2 -translate-x-1/2">
				<a href="#about" aria-label="Scroll to About section">
					<motion.div
						animate={{ y: [0, 8, 0] }}
						transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
						className="text-muted hover:text-accent transition-colors">
						<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
						</svg>
					</motion.div>
				</a>
			</div>
		</section>
	);
};

export default Hero;
